import {
  AdditiveBlending,
  BoxGeometry,
  Color,
  DoubleSide,
  Group,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Uniform,
} from "three";
import type { UnderwaterVisualSettings } from "./VisualSettings";

type RayConfig = {
  x: number;
  z: number;
  width: number;
  height: number;
  rotationY: number;
  tiltZ: number;
  opacity: number;
  phase: number;
};

const RAY_CONFIGS: RayConfig[] = [];

export class UnderwaterLightRays extends Group {
  private timeUniform = new Uniform(0);
  private rays: Mesh[] = [];
  private baseCenterY = 0;

  constructor(roomSize: number, roomHeight: number, baseCameraHeight: number, count: number) {
    super();

    this.name = "underwater-light-rays";
    this.renderOrder = 20;

    const topY = roomHeight / 2 + 4.2;
    const bottomY = -baseCameraHeight - 3.2;
    const centerY = (topY + bottomY) / 2;
    this.baseCenterY = centerY;
    const heightScale = Math.max(roomSize / 5, 1);

    RAY_CONFIGS.length = 0;

    for (let i = 0; i < count; i++) {

      const angle = i * Math.PI * 2 / count;
    
      RAY_CONFIGS.push({
        x: Math.cos(angle) * (1.5 + Math.random() * 4.5),
        z: Math.sin(angle) * (1.5 + Math.random() * 4.5),
        width: 1.5 + Math.random() * 1.5,
        height: 6,
        rotationY: Math.atan2(Math.sin(angle), Math.cos(angle)) + Math.random() * 0.3,
        tiltZ: -0.3 + Math.random() * 0.3,
        opacity: 0.3 + Math.random() * 0.7,
        phase: Math.random() * 10.0,
      });
    }

    RAY_CONFIGS.forEach((config) => {
      const geometry = new PlaneGeometry(
        config.width * heightScale,
        config.height * heightScale,
        1,
        1
      );
      const material = new ShaderMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: true,
        blending: AdditiveBlending,
        side: DoubleSide,
        uniforms: {
          time: this.timeUniform,
          rayColor: new Uniform(new Color("#c7fbff")),
          opacity: new Uniform(config.opacity),
          phase: new Uniform(config.phase),
        },
        vertexShader: /* glsl */ `
          varying vec2 vUv;

          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          uniform float time;
          uniform vec3 rayColor;
          uniform float opacity;
          uniform float phase;
          varying vec2 vUv;

          void main() {
            float wave = sin(time * 0.42 + phase + vUv.y * 4.5) * 0.08;
            float center = 0.5 + wave;
            float beamWidth = mix(0.42, 0.10, vUv.y);
            float beam = 1.0 - smoothstep(beamWidth * 0.34, beamWidth, abs(vUv.x - center));
            float softEdges = smoothstep(0.0, 0.18, vUv.x) * smoothstep(1.0, 0.82, vUv.x);
            float bottomFade = smoothstep(0.0, 0.22, vUv.y);
            float topFade = 1.0 - smoothstep(0.76, 0.98, vUv.y);
            float verticalMask = bottomFade * topFade;
            float breakup =
              0.72 +
              sin(time * 0.58 + phase + vUv.y * 17.0 + vUv.x * 5.5) * 0.18 +
              sin(time * 0.37 + phase * 1.7 + vUv.y * 29.0) * 0.1;
            float shimmer = 0.78 + sin(time * 0.9 + phase + vUv.y * 1.0) * 0.22;
            float alpha = beam * softEdges * verticalMask * shimmer * breakup * opacity;

            gl_FragColor = vec4(rayColor, alpha);
          }
        `,
      });

      const ray = new Mesh(geometry, material);
      ray.position.set(config.x, centerY, config.z);
      ray.rotation.set(0, config.rotationY, config.tiltZ);
      ray.renderOrder = this.renderOrder;
      this.add(ray);
      this.rays.push(ray);
    });
  }

  setSettings(settings: UnderwaterVisualSettings) {
    const visibleCount = Math.max(
      0,
      Math.min(this.rays.length, Math.round(settings.lightRaysCount))
    );

    this.rays.forEach((ray, index) => {
      ray.visible = index < visibleCount && settings.lightRaysOpacity > 0;
      ray.scale.x = settings.lightRaysWidth;
      ray.scale.y = settings.lightRaysLength;
      ray.position.y = this.baseCenterY + settings.lightRaysVerticalOffset;

      const material = ray.material;
      if (material instanceof ShaderMaterial) {
        const baseOpacity = RAY_CONFIGS[index]?.opacity ?? 0.3;
        // const baseOpacity = 1;
        material.uniforms.opacity.value = baseOpacity * settings.lightRaysOpacity;
        material.uniforms.rayColor.value.set(settings.lightRaysColor);
      }
    });
  }

  update(time: number, speed = 1) {
    this.timeUniform.value = time * speed;
  }
}
